import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { Optional } from '../../../Common/interfaces';
import { Log } from '../../../Common/Log';
import {
    ExerciseDto,
    ModuleDto,
    Program,
    Question,
} from '../../modules/interfaces';
import { exercisesAtom } from '../../modules/data';
import { getExerciseData } from '../../modules/exercises';
import { urlParamsAtom } from './url';

export { exercisesAtom };

export const isFullHeightEntryFieldsSupportedAtom = atomWithStorage(
    'isFullHeightEntryFieldsSupported',
    false,
    createJSONStorage(() => sessionStorage),
);

export const initialProgramAtom = atom<Program>();
export const programAtom = atom<Program>((get) => {
    const urlParams = get(urlParamsAtom);
    const initialProgram = get(initialProgramAtom);
    if (urlParams.program) {
        switch (urlParams.program) {
            case 'tanke':
                return Program.Tanke;
            case 'pres':
                return Program.Pres;
            case 'stress':
                return Program.Stress;
            default:
                Log.error(`Unknown program from URL: ${urlParams.program}`);
                return initialProgram;
        }
    } else {
        return initialProgram;
    }
});
export const modulesAtom = atom<ModuleDto[]>();

export const exercisesWhichCanContainDataAtom = atom<ExerciseDto[]>((get) => {
    const exercises = get(exercisesAtom);
    const modules = get(modulesAtom);
    return exercises
        .map((exercise) => {
            const module = modules.find((x) => x.Id == exercise.ModuleId.Value);
            const data = getExerciseData(module.Program, exercise.Code);
            if (data == null || data.dataKeys.length === 0) {
                return null;
            }

            return exercise;
        })
        .filter(Boolean);
});

export const exercisesWithAnswersAtom = atom<
    { exercise: ExerciseDto; answers: string[] }[]
>((get) => {
    const exercises = get(exercisesAtom);
    const modules = get(modulesAtom);
    return exercises
        .map((exercise) => {
            const module = modules.find((x) => x.Id == exercise.ModuleId.Value);
            const data = getExerciseData(module.Program, exercise.Code);
            if (data == null || data.dataKeys.length === 0) {
                return null;
            }
            const answers = getExerciseAnswers(exercise, data.questions) ?? [];
            if (answers.length === 0 && !data.haveDataForLogbook?.(exercise)) {
                return null;
            }

            return {
                exercise,
                answers,
            };
        })
        .filter(Boolean);
});

export const modulesWithExercisesThatCanContainDataAtom = atom<ModuleDto[]>(
    (get) => {
        const modules = get(modulesAtom);
        const exercises = get(exercisesWhichCanContainDataAtom);

        return modules.filter(
            (module) => !!exercises.find((x) => x.ModuleId.Value === module.Id),
        );
    },
);

export const modulesWithAvailableDataAtom = atom<ModuleDto[]>((get) => {
    const modules = get(modulesAtom);
    const exerciseData = get(exercisesWithAnswersAtom);

    return modules.filter(
        (module) =>
            !!exerciseData.find(
                (data) => data.exercise.ModuleId.Value === module.Id,
            ),
    );
});

function getExerciseAnswers(
    exercise: ExerciseDto,
    keys: Optional<Question[]>,
): Optional<string[]> {
    return keys
        ?.flatMap((x) => {
            const data =
                x.summary?.(exercise) ??
                x.dataKeys.map((x) => exercise.Data[x]).filter(Boolean);
            return x.isArray ? data.flatMap((x) => JSON.parse(x)) : data;
        })
        .filter(Boolean);
}
